import { supabase } from '../../supabase';
import type { AppBoleta } from '../../types/planManejo';

// ─── Recalcular una cédula con el área/plantas ACTUAL del catálogo ──────────────
// Caso de uso: se corrigió el área (o plantas) de un bloque en el catálogo y la
// cédula ya generada quedó con los valores viejos "congelados" (área, plantas y
// todas las cantidades = dosis × área). Este recálculo re-lee el área/plantas
// vigentes de emp_bloques, recalcula agua/booms/densidad y cada cantidad_aplicar
// usando la DOSIS ya guardada en cada línea, y regresa la boleta a 'pendiente'
// para que bodega la vuelva a despachar. NO anula ni renumera la cédula.

export interface ResultadoRecalculo {
  ok: boolean;
  error?: string;
  areaAnterior?: number;
  areaNueva?: number;
  plantasAnterior?: number;
  plantasNueva?: number;
  lineasActualizadas?: number;
}

// Compara números de bloque de forma laxa: "1" == "01" == 1.
function normNum(v: unknown): string {
  return String(v ?? '').trim().replace(/^0+(?=\d)/, '');
}

export async function recalcularBoleta(
  boleta: AppBoleta,
  empresaId: number,
): Promise<ResultadoRecalculo> {
  try {
    if (boleta.id <= 0) {
      return { ok: false, error: 'Solo se pueden recalcular cédulas ERP (no histórico VFP).' };
    }
    if (boleta.manual) {
      return { ok: false, error: 'Las boletas manuales no se recalculan (área/plantas capturados a mano).' };
    }
    if (boleta.estado === 'aplicada' || boleta.estado === 'cancelada') {
      return { ok: false, error: 'Esta cédula ya fue aplicada o cancelada; bodega ya movió inventario contra las cantidades actuales. Recalcularla la desincronizaría del inventario ya descontado.' };
    }
    if (!boleta.lote_id) {
      return { ok: false, error: 'La cédula no tiene lote asociado; no se puede re-leer el área.' };
    }

    // 1. Números de bloque que componen la cédula (campo texto "1,2,3,...").
    const numsBoleta = String(boleta.bloques ?? '')
      .split(/[,;\s]+/)
      .map(normNum)
      .filter(Boolean);
    if (numsBoleta.length === 0) {
      return { ok: false, error: 'La cédula no tiene bloques registrados.' };
    }

    // 2. Re-leer área/plantas VIGENTES de los bloques de ese lote.
    const { data: bloques, error: bErr } = await supabase
      .from('emp_bloques')
      .select('num, area_ha, plant_count')
      .eq('empresa_id', empresaId)
      .eq('lote_id', boleta.lote_id);
    if (bErr) return { ok: false, error: `Bloques: ${bErr.message}` };

    const setBoleta = new Set(numsBoleta);
    const usados = (bloques ?? []).filter(b => setBoleta.has(normNum((b as any).num)));
    if (usados.length === 0) {
      return { ok: false, error: 'No se encontraron los bloques de la cédula en el catálogo actual.' };
    }

    const areaNueva = usados.reduce((s, b) => s + Number((b as any).area_ha ?? 0), 0);
    const plantasNueva = usados.reduce((s, b) => s + Number((b as any).plant_count ?? 0), 0);

    const areaAnterior = Number(boleta.area_ha ?? 0);
    const plantasAnterior = Number(boleta.plantas ?? 0);

    // 3. Recalcular magnitudes de cabecera dependientes del área.
    const aguaL = Number(boleta.agua_l ?? 0);
    const boomCap = Number(boleta.boom_capacidad_l ?? 0);
    const tieneBoom = boomCap > 0;
    const aguaTotal = aguaL * areaNueva;
    const densidad = areaNueva > 0 ? plantasNueva / areaNueva : 0;
    const nBooms = tieneBoom ? Math.floor(aguaTotal / boomCap) : null;
    const fraccion = tieneBoom ? aguaTotal % boomCap : null;

    // 4. Traer las líneas de producto y recalcular cada cantidad con su dosis guardada.
    const { data: lineas, error: lErr } = await supabase
      .from('app_boleta_productos')
      .select('id, dosis, volumen, es_agua')
      .eq('boleta_id', boleta.id)
      .eq('empresa_id', empresaId);
    if (lErr) return { ok: false, error: `Productos: ${lErr.message}` };

    for (const ln of (lineas ?? [])) {
      const l = ln as any;
      const dosis = Number(l.dosis ?? 0);
      if (l.es_agua) {
        // El agua: cantidad = agua_l × area; boom/fracción como en la generación.
        await supabase.from('app_boleta_productos').update({
          cantidad_aplicar: aguaTotal,
          cantidad_boom: tieneBoom && aguaTotal >= boomCap ? boomCap : 0,
          cantidad_fraccion: tieneBoom ? aguaTotal % boomCap : 0,
        }).eq('id', l.id);
        continue;
      }
      const dosisTotal = dosis * areaNueva;
      // Tasa por UN boom completo. Sin volumen de referencia manda el agua de
      // la boleta; con 1 la cantidad por boom se dispararía a dosis × capacidad.
      const boomPorBoom = tieneBoom && aguaL > 0
        ? (dosis / (Number(l.volumen) || aguaL)) * boomCap
        : 0;
      // cantidad_boom solo se llena si el agua total alcanza para un boom completo.
      const boomProd = aguaTotal >= boomCap ? boomPorBoom : 0;
      // Fracción vía boomPorBoom (no boomProd): sin boom completo da directamente
      // dosisTotal — toda la dosis va en la tanqueada parcial.
      const fraccionProd = boomPorBoom > 0 ? dosisTotal % boomPorBoom : 0;
      await supabase.from('app_boleta_productos').update({
        cantidad_aplicar: dosisTotal,
        cantidad_boom: boomProd,
        cantidad_fraccion: fraccionProd,
      }).eq('id', l.id);
    }

    // 5. Actualizar la cabecera y regresar a 'pendiente' para bodega.
    const { error: uErr } = await supabase.from('app_boletas').update({
      area_ha: areaNueva,
      plantas: plantasNueva,
      densidad,
      agua_total_l: aguaTotal,
      n_booms_completos: nBooms,
      litros_fraccion: fraccion,
      estado: 'pendiente',
    }).eq('id', boleta.id).eq('empresa_id', empresaId);
    if (uErr) return { ok: false, error: `Cabecera: ${uErr.message}` };

    return {
      ok: true,
      areaAnterior, areaNueva,
      plantasAnterior, plantasNueva,
      lineasActualizadas: (lineas ?? []).length,
    };
  } catch (e: any) {
    return { ok: false, error: e?.message || 'Error inesperado al recalcular.' };
  }
}
